import React, { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { useColors } from "../context/ThemeContext";

/**
 * Camera-based QR scanner for ticket check-in.
 * Calls onScan(decodedText) once per successful read; pauses briefly so callers can validate.
 */
export default function TicketQrScanner({ onScan, paused = false }) {
  const C = useColors();
  const scannerRef = useRef(null);
  const busyRef = useRef(false);
  const onScanRef = useRef(onScan);
  const [error, setError] = useState("");
  const [starting, setStarting] = useState(true);
  const elementId = "pendpay-ticket-qr-reader";

  useEffect(() => {
    onScanRef.current = onScan;
  }, [onScan]);

  useEffect(() => {
    let cancelled = false;
    const scanner = new Html5Qrcode(elementId);
    scannerRef.current = scanner;

    const start = async () => {
      setStarting(true);
      setError("");
      try {
        await scanner.start(
          { facingMode: "environment" },
          {
            fps: 8,
            qrbox: (viewfinderWidth, viewfinderHeight) => {
              const edge = Math.min(viewfinderWidth, viewfinderHeight) * 0.72;
              return { width: edge, height: edge };
            },
            aspectRatio: 1,
          },
          (decoded) => {
            if (busyRef.current || paused || !decoded) return;
            busyRef.current = true;
            Promise.resolve(onScanRef.current?.(decoded)).finally(() => {
              setTimeout(() => {
                busyRef.current = false;
              }, 2200);
            });
          },
          () => {}
        );
        if (!cancelled) setStarting(false);
      } catch (err) {
        if (!cancelled) {
          setStarting(false);
          setError(
            err?.message?.includes("Permission")
              ? "Camera permission is required to scan tickets."
              : err?.message || "Could not start camera. Use HTTPS or allow camera access."
          );
        }
      }
    };

    start();

    return () => {
      cancelled = true;
      const s = scannerRef.current;
      scannerRef.current = null;
      if (s) {
        s.stop()
          .then(() => s.clear())
          .catch(() => {});
      }
    };
  }, []);

  useEffect(() => {
    busyRef.current = !!paused;
  }, [paused]);

  return (
    <div style={{ marginBottom: 16 }}>
      <div
        style={{
          position: "relative",
          borderRadius: 16,
          overflow: "hidden",
          border: `1px solid ${C.border}`,
          background: C.card,
          minHeight: 280,
        }}
      >
        <div id={elementId} style={{ width: "100%" }} />
        {starting && !error && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: C.card,
              color: C.muted,
              fontSize: 13,
              fontWeight: 700,
            }}
          >
            Starting camera…
          </div>
        )}
        {/* Scan frame overlay */}
        {!error && (
          <div
            style={{
              pointerEvents: "none",
              position: "absolute",
              inset: "18%",
              border: `2px solid ${C.lime}`,
              borderRadius: 16,
              boxShadow: `0 0 0 9999px ${C.bg}88`,
            }}
          />
        )}
      </div>
      {error && (
        <p style={{ color: C.danger, fontSize: 13, fontWeight: 600, marginTop: 10 }}>{error}</p>
      )}
      <p style={{ color: C.muted, fontSize: 12, marginTop: 10, textAlign: "center" }}>
        Point the camera at the ticket QR code
      </p>
    </div>
  );
}
